import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { Alert, Card, CardContent, CardHeader, Stack } from "../../design-system";
import { colors } from "../../design-system";
import InteractiveLineChart from "../charts/InteractiveLineChart";

const PerformanceTrends = ({ players = [] }) => {
  const { t } = useTranslation('scouting');
  const theme = useTheme();
  const lineColors = useMemo(() => theme.palette.mode === "dark"
    ? ["#55d8c1", "#a8c0df", "#e4c47d", "#b4bbaa", "#f0f5eb"]
    : ["#087f78", "#596e85", "#896519", "#62685e", "#172b21"], [theme.palette.mode]);
  const METRIC_OPTIONS = [
    { key: "matches", label: t('components.trends.metric.matches') },
    { key: "goals", label: t('components.trends.metric.goals') },
    { key: "starters", label: t('components.trends.metric.starters', 'Titularidades') },
    { key: "minutes", label: t('components.trends.metric.minutes') },
  ];
  const [metric, setMetric] = useState("matches");
  const [selectedIds, setSelectedIds] = useState([]);

  const allPlayersWithTrends = useMemo(
    () => players.filter((player) => Array.isArray(player.trendSeries) && player.trendSeries.length > 0),
    [players]
  );

  // Auto-select all players with trends on first load
  useEffect(() => {
    setSelectedIds((current) => {
      const validIds = current.filter((id) => allPlayersWithTrends.some((p) => p.id === id));
      if (validIds.length >= 1) return validIds;
      return allPlayersWithTrends.map((p) => p.id);
    });
  }, [allPlayersWithTrends]);

  const playersWithTrends = useMemo(
    () => allPlayersWithTrends.filter((p) => selectedIds.includes(p.id)),
    [allPlayersWithTrends, selectedIds]
  );

  const chartData = useMemo(() => {
    const points = new Map();

    playersWithTrends.forEach((player, playerIndex) => {
      player.trendSeries.forEach((point, index) => {
        const sortValue = point.sortValue ?? index;
        const key = `${sortValue}-${point.label}`;
        if (!points.has(key)) {
          points.set(key, {
            name: point.label,
            __sortValue: typeof sortValue === "number" ? sortValue : index,
          });
        }
        points.get(key)[`player_${playerIndex}`] = point[metric] ?? 0;
      });
    });

    return Array.from(points.values())
      .sort((left, right) => left.__sortValue - right.__sortValue)
      .map(({ __sortValue, ...rest }) => rest);
  }, [metric, playersWithTrends]);

  const dataKeys = useMemo(
    () =>
      playersWithTrends.map((player, index) => ({
        dataKey: `player_${index}`,
        name: player.name,
        color: lineColors[allPlayersWithTrends.findIndex((entry) => entry.id === player.id) % lineColors.length],
      })),
    [playersWithTrends, allPlayersWithTrends, lineColors]
  );

  const metricLabel = METRIC_OPTIONS.find((option) => option.key === metric)?.label || "Goles";

  return (
    <Card className="sc-chart-panel" variant="outlined">
      <CardHeader
        title={t('components.trends.title')}
        subheader={t('components.trends.subtitle')}
      />
      <CardContent>
        <Stack spacing={3}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={metric}
              onChange={(_, nextMetric) => nextMetric && setMetric(nextMetric)}
            >
              {METRIC_OPTIONS.map((option) => (
                <ToggleButton key={option.key} value={option.key} sx={{ textTransform: "none", fontWeight: 700 }}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Typography variant="caption" color={'text.secondary'}>
              {t('components.trends.withData', { count: playersWithTrends.length })}
            </Typography>
          </Box>

          {allPlayersWithTrends.length > 0 && (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={allPlayersWithTrends}
              value={playersWithTrends}
              onChange={(_, value) => setSelectedIds(value.map((p) => p.id))}
              getOptionLabel={(option) => option?.name || "Jugador"}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <Box component="li" key={key} {...optionProps} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: colors.primary[100], color: colors.primary[700], fontSize: 12 }}>
                      {option.name?.charAt(0)?.toUpperCase() || "?"}
                    </Avatar>
                    <Typography variant="body2">{option.name}</Typography>
                  </Box>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      borderLeft: `3px solid ${lineColors[allPlayersWithTrends.findIndex((p) => p.id === option.id) % lineColors.length]}`,
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('components.trends.selectPlayers', 'Seleccionar jugadores')}
                  size="small"
                />
              )}
            />
          )}

          {playersWithTrends.length === 0 || chartData.length === 0 ? (
            <Alert severity="info" title={t('components.trends.noData')}>
              {t('components.trends.noData')}
            </Alert>
          ) : (
            <InteractiveLineChart
              title={t('components.trends.chartTitle', { metric: metricLabel })}
              data={chartData}
              dataKeys={dataKeys}
              height={280}
              strokeWidth={2}
              lineType="linear"
              animated={false}
              allowDecimals={false}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PerformanceTrends;
